/**
 * Validation de complétude d'une formation (admin).
 *
 * Règles :
 *  - Les champs obligatoires doivent être renseignés au moins dans la langue par défaut.
 *  - Parité de traduction : si un champ multilingue est rempli dans la langue par défaut
 *    (ou dans n'importe quelle langue activée), il doit l'être dans TOUTES les langues activées.
 *  - Un champ optionnel entièrement vide est considéré comme valide.
 *
 * Le parcours couvre l'ensemble de l'arbre : formation → sections → items
 * (capsules + leurs modules, quiz + questions + options, ateliers).
 */

export type MultilingualText = { [lang: string]: string };

export type ValidationIssueKind = 'required' | 'translation' | 'structural';

export interface ValidationIssue {
  /** Emplacement lisible, ex: "Section 2 « Introduction » › Capsule 1 « ... » › Module 3 (Titre)" */
  path: string;
  /** Nom du champ concerné, ex: "Titre", "Description" */
  field: string;
  kind: ValidationIssueKind;
  /** Codes des langues manquantes (pour les champs multilingues) */
  missingLanguages?: string[];
  /** Message complémentaire pour les problèmes structurels */
  detail?: string;
}

export interface ValidationResult {
  issues: ValidationIssue[];
  defaultLanguage: string;
  checkedLanguages: string[];
}

/** Considère une valeur texte (potentiellement HTML) comme vide. */
const isBlank = (value?: string): boolean => {
  if (value === undefined || value === null) return true;
  const stripped = String(value)
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\u00a0/g, ' ')
    .trim();
  return stripped.length === 0;
};

const MODULE_TYPE_LABELS: Record<string, string> = {
  title: 'Titre',
  richtext: 'Texte enrichi',
  video: 'Vidéo',
  audio: 'Audio',
  file: 'Fichier',
  question: 'Question',
  citation: 'Citation',
  link: 'Lien',
  notification: 'Box highlight',
  image: 'Image',
};

export class CourseValidator {
  private issues: ValidationIssue[] = [];
  private languages: string[];
  private defaultLanguage: string;

  constructor(availableLanguages: string[], defaultLanguage: string) {
    // On déduplique en gardant la langue par défaut en tête.
    const langs = Array.isArray(availableLanguages) ? availableLanguages.filter(Boolean) : [];
    const unique = Array.from(new Set([defaultLanguage, ...langs].filter(Boolean)));
    this.languages = unique;
    this.defaultLanguage = defaultLanguage;
  }

  /**
   * Vérifie un champ multilingue.
   * @param required si vrai, le champ doit être rempli (au moins en langue par défaut).
   */
  private checkField(
    path: string,
    field: string,
    value: MultilingualText | undefined,
    required: boolean
  ): void {
    const val = value || {};
    const defaultFilled = !isBlank(val[this.defaultLanguage]);
    const anyFilled = this.languages.some((lang) => !isBlank(val[lang]));

    // Champ optionnel et totalement vide → rien à signaler.
    if (!required && !defaultFilled && !anyFilled) return;

    const missing = this.languages.filter((lang) => isBlank(val[lang]));
    if (missing.length === 0) return;

    const defaultMissing = isBlank(val[this.defaultLanguage]);
    const kind: ValidationIssueKind =
      required && defaultMissing ? 'required' : 'translation';

    this.issues.push({ path, field, kind, missingLanguages: missing });
  }

  private addStructural(path: string, field: string, detail: string): void {
    this.issues.push({ path, field, kind: 'structural', detail });
  }

  private pickLabel(value: MultilingualText | undefined, fallback: string): string {
    const val = value || {};
    const candidate =
      val[this.defaultLanguage] ||
      Object.values(val).find((v) => !isBlank(v));
    if (candidate && !isBlank(candidate)) {
      const stripped = candidate.replace(/<[^>]*>/g, '').trim();
      return stripped.length > 40 ? `${stripped.slice(0, 40)}…` : stripped;
    }
    return fallback;
  }

  private validateModule(basePath: string, module: any, index: number): void {
    const typeLabel = MODULE_TYPE_LABELS[module?.type] || module?.type || 'Module';
    const path = `${basePath} › Module ${index + 1} (${typeLabel})`;
    const content = module?.content || {};

    switch (module?.type) {
      case 'title':
        this.checkField(path, 'Titre', content.title, true);
        break;
      case 'richtext':
        this.checkField(path, 'Texte', content.text, true);
        break;
      case 'video':
      case 'audio':
        this.checkField(path, 'URL du média', content.fileUrl, true);
        break;
      case 'file':
        this.checkField(path, 'Fichier (URL)', content.fileUrl, true);
        this.checkField(path, 'Nom du fichier', content.fileName, false);
        break;
      case 'question':
        this.checkField(path, 'Question', content.question, true);
        if (Array.isArray(content.options)) {
          content.options.forEach((opt: MultilingualText, optIdx: number) => {
            this.checkField(path, `Option ${optIdx + 1}`, opt, true);
          });
        }
        break;
      case 'citation':
        this.checkField(path, 'Citation', content.content, true);
        this.checkField(path, 'Auteur', content.name, false);
        this.checkField(path, 'Description auteur', content.description, false);
        break;
      case 'link':
        this.checkField(path, 'URL', content.url, true);
        this.checkField(path, 'Texte du lien', content.text, false);
        break;
      case 'notification':
        // L'éditeur admin persiste le corps dans content.content (content.text = ancien format).
        this.checkField(path, 'Contenu', content.content ?? content.text, true);
        break;
      case 'image':
        this.checkField(path, 'Image (par langue)', content.imageByLang, true);
        break;
      default:
        break;
    }
  }

  private validateCapsule(basePath: string, capsule: any): void {
    this.checkField(basePath, 'Titre', capsule?.title, true);
    this.checkField(basePath, 'Description', capsule?.description, true);
    const modules = Array.isArray(capsule?.modules) ? capsule.modules : [];
    modules.forEach((mod: any, idx: number) => this.validateModule(basePath, mod, idx));
  }

  private validateQuiz(basePath: string, quiz: any): void {
    this.checkField(basePath, 'Titre', quiz?.title, true);
    this.checkField(basePath, 'Label', quiz?.description, false);
    this.checkField(basePath, 'Détails', quiz?.details, false);

    const isDiagnostic = Boolean(quiz?.isDiagnostic);
    if (!isDiagnostic && (quiz?.passingPercentage === undefined || quiz?.passingPercentage === null || quiz?.passingPercentage === '')) {
      this.addStructural(basePath, 'Pourcentage de réussite', 'Non défini pour un quiz noté.');
    }

    const questions = Array.isArray(quiz?.questions) ? quiz.questions : [];
    if (questions.length === 0) {
      this.addStructural(basePath, 'Questions', 'Le quiz ne contient aucune question.');
    }

    questions.forEach((q: any, qIdx: number) => {
      const qPath = `${basePath} › Question ${qIdx + 1}`;
      this.checkField(qPath, 'Énoncé', q?.question, true);

      const options = Array.isArray(q?.options) ? q.options : [];
      const filledOptions = options.filter((opt: MultilingualText) =>
        this.languages.some((lang) => !isBlank((opt || {})[lang]))
      );
      if (filledOptions.length < 2) {
        this.addStructural(qPath, 'Options', 'Au moins 2 options sont requises.');
      }
      options.forEach((opt: MultilingualText, optIdx: number) => {
        this.checkField(qPath, `Option ${optIdx + 1}`, opt, true);
      });

      if (!isDiagnostic && (q?.correctAnswerIndex === undefined || q?.correctAnswerIndex === null)) {
        this.addStructural(qPath, 'Bonne réponse', 'Aucune bonne réponse sélectionnée.');
      }
    });
  }

  private validateWorkshop(basePath: string, workshop: any): void {
    this.checkField(basePath, 'Titre', workshop?.title, true);
    this.checkField(basePath, 'Description', workshop?.description, true);
    this.checkField(basePath, 'Détails', workshop?.details, false);
    this.checkField(basePath, 'Lieu', workshop?.lieu, false);
  }

  /** Question d'évaluation (diagnostic / impact / suivi / question liée). */
  private validateAssessmentQuestion(
    basePath: string,
    question: any,
    index: number,
    label: string = 'Question'
  ): void {
    const path = `${basePath} › ${label} ${index + 1}`;
    this.checkField(path, 'Énoncé', question?.question, true);

    if (question?.type === 'weighted-mcq') {
      const options = Array.isArray(question?.options) ? question.options : [];
      if (options.length < 2) {
        this.addStructural(path, 'Options', 'Un QCM pondéré doit avoir au moins 2 options.');
      }
      options.forEach((opt: any, optIdx: number) => {
        this.checkField(path, `Option ${optIdx + 1}`, opt?.text, true);
      });
    }

    if (question?.type === 'scale') {
      const scale = question?.scale;
      if (!scale || scale.min === undefined || scale.min === null || scale.max === undefined || scale.max === null) {
        this.addStructural(path, 'Échelle', 'Les bornes min et max sont requises.');
      } else if (scale.min >= scale.max) {
        this.addStructural(path, 'Échelle', 'Le min doit être strictement inférieur au max.');
      }
      this.checkField(path, 'Libellé min', scale?.minLabel, false);
      this.checkField(path, 'Libellé max', scale?.maxLabel, false);
    }
  }

  private validateAssessment(assessment: any): void {
    if (!assessment) return;
    const root = 'Diagnostic & Impact';

    const linked = Array.isArray(assessment?.linkedQuestions) ? assessment.linkedQuestions : [];
    linked.forEach((q: any, idx: number) =>
      this.validateAssessmentQuestion(`${root} › Questions liées`, q, idx, 'Question liée')
    );

    this.checkField(`${root} › Diagnostic initial`, 'Description', assessment?.initialDiagnosticDescription, false);
    const diagQuestions = Array.isArray(assessment?.initialDiagnosticQuestions)
      ? assessment.initialDiagnosticQuestions
      : [];
    diagQuestions.forEach((q: any, idx: number) =>
      this.validateAssessmentQuestion(`${root} › Diagnostic initial`, q, idx)
    );

    this.checkField(`${root} › Mesure d'impact finale`, 'Description', assessment?.finalImpactDescription, false);
    const finalQuestions = Array.isArray(assessment?.finalImpactQuestions)
      ? assessment.finalImpactQuestions
      : [];
    finalQuestions.forEach((q: any, idx: number) =>
      this.validateAssessmentQuestion(`${root} › Mesure d'impact finale`, q, idx)
    );

    const followups = Array.isArray(assessment?.followupImpacts) ? assessment.followupImpacts : [];
    followups.forEach((f: any, fIdx: number) => {
      const label = this.pickLabel(f?.label, 'Sans libellé');
      const fPath = `${root} › Mesure de suivi ${fIdx + 1} « ${label} »`;
      this.checkField(fPath, 'Libellé', f?.label, true);
      this.checkField(fPath, 'Description', f?.description, false);
      const fQuestions = Array.isArray(f?.questions) ? f.questions : [];
      fQuestions.forEach((q: any, idx: number) =>
        this.validateAssessmentQuestion(fPath, q, idx)
      );
    });
  }

  validate(course: any, assessment?: any): ValidationResult {
    this.issues = [];

    // Niveau formation
    this.checkField('Formation', 'Titre', course?.title, true);
    this.checkField('Formation', 'Description', course?.description, true);

    const chapters = Array.isArray(course?.chapters) ? course.chapters : [];
    chapters.forEach((chapter: any, chIdx: number) => {
      const chLabel = this.pickLabel(chapter?.title, 'Sans titre');
      const chPath = `Section ${chIdx + 1} « ${chLabel} »`;

      this.checkField(chPath, 'Titre', chapter?.title, true);
      this.checkField(chPath, 'Description', chapter?.description, false);
      this.checkField(chPath, 'Résumé', chapter?.summary, true);

      const items = Array.isArray(chapter?.items) ? chapter.items : [];
      items.forEach((item: any, itemIdx: number) => {
        const itemLabel = this.pickLabel(item?.title, 'Sans titre');
        if (item?.itemType === 'capsule') {
          this.validateCapsule(`${chPath} › Capsule ${itemIdx + 1} « ${itemLabel} »`, item);
        } else if (item?.itemType === 'quiz') {
          this.validateQuiz(`${chPath} › Quiz ${itemIdx + 1} « ${itemLabel} »`, item);
        } else if (item?.itemType === 'workshop') {
          this.validateWorkshop(`${chPath} › Atelier ${itemIdx + 1} « ${itemLabel} »`, item);
        }
      });
    });

    // Niveau Diagnostic & Impact (uniquement si activé / fourni)
    this.validateAssessment(assessment);

    return {
      issues: this.issues,
      defaultLanguage: this.defaultLanguage,
      checkedLanguages: this.languages,
    };
  }
}

export function validateCourseCompleteness(
  course: any,
  availableLanguages: string[],
  defaultLanguage: string,
  assessment?: any
): ValidationResult {
  return new CourseValidator(availableLanguages, defaultLanguage).validate(course, assessment);
}
